import { LoadingOutlined } from '@ant-design/icons'
import {
  Collapse,
  CollapseProps,
  Divider,
  Pagination,
  Spin,
  Typography,
} from 'antd'
import Layout, { Content } from 'antd/es/layout/layout'
import {
  DrilldownPaginationType,
  useFilteredEntryQuery,
  usePaginatedQuery,
} from 'features/drilldown'
import {
  createClassificationIndicationConfigContextValue,
  DEFAULT_CLASSIFICATION_CONFIG,
  entryToIndication,
  entryToIndicationSet,
  IndicationClassificationConfigContext,
  IndicationSet,
  IndicationTriageList,
  RD_TYPE_INDICATION,
  RD_TYPE_INDICATION_SET,
  RolodexIndicationContextProvider,
  ToggledIndicationDetailView,
} from 'features/indicationTriage'
import { useRolodexEntries } from 'features/rolodex'
import _ from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponent,
  GeckoUnit,
  GenericPanelData,
  RolodexEntryType,
} from 'types'

import { HoverCollapseSider } from './HoverCollapseSider'
import { IndicationClassificationCountDisplay } from './IndicationCountDisplay'
import { OrganizationSelectSidebar } from './OrganizationSelectSidebar'

const ROLODEX_LIMIT = 10000

const { Title } = Typography

export const DEFAULT_INDICATION_PANEL_DATA: GenericPanelData = {
  isPinned: false,
  title: 'Indications',
  panelDataSchemaVersion: 1,
}

const RELEVANT_TYPES = [
  RolodexEntryType.ORGANIZATION,
  RolodexEntryType.SITE,
  RolodexEntryType.UNIT,
  RolodexEntryType.COMPONENT,
  RolodexEntryType.INDICATION_SET,
  RolodexEntryType.INDICATION,
]

const RawIndicationListPanel: FC = () => {
  const { query: rawEntryQuery, isLoading: queryLoading } =
    useFilteredEntryQuery(RD_TYPE_INDICATION_SET)
  const entryQuery = useMemo(
    () =>
      rawEntryQuery
        ? {
            ...rawEntryQuery,
            limit: ROLODEX_LIMIT,
            links: _.uniq([
              ...(rawEntryQuery.links ?? []),
              ...RELEVANT_TYPES,
            ]).filter((t) => t !== rawEntryQuery.type),
          }
        : undefined,
    [rawEntryQuery]
  )
  const {
    entries,
    organizations,
    sites,
    units,
    components,
    isLoading: entriesLoading,
  } = useRolodexEntries(entryQuery)
  const { indicationSets } = useMemo(() => {
    const indicationSets = entries
      .filter((e) => e.type === RD_TYPE_INDICATION_SET)
      .map(entryToIndicationSet)
    return { indicationSets }
  }, [entries])

  const isLoading = entriesLoading || queryLoading

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  useEffect(() => {
    if (
      organizations.length &&
      !organizations.find((o) => o.id === selectedOrgId)
    ) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [organizations, selectedOrgId])

  const { unitToComponents, unitToISets } = useMemo(() => {
    const unitToComponents = _.groupBy(components, (comp) => comp.tags.unit)
    const unitToISets = _.groupBy(indicationSets, (iSet) => iSet.tags.unit)
    return { unitToComponents, unitToISets }
  }, [components, indicationSets])

  const { unitToSite } = useMemo(() => {
    const unitToSite = new Map(
      units.map((unit) => [unit.id, sites.find((s) => unit.tags.site === s.id)])
    )
    return { unitToSite }
  }, [sites, units])

  return (
    <Layout>
      {organizations.length > 1 && (
        <HoverCollapseSider style={{ marginRight: '1rem' }}>
          <OrganizationSelectSidebar
            organizations={organizations}
            indicationSets={indicationSets}
            isLoading={isLoading}
            selectedOrgId={selectedOrgId}
            onSelect={setSelectedOrgId}
          />
        </HoverCollapseSider>
      )}
      <Layout>
        <Content>
          <Spin spinning={isLoading}>
            <>
              {units
                .filter((unit) => unit.tags.organization === selectedOrgId)
                .map((unit) => {
                  const site = unitToSite.get(unit.id)
                  const components = unitToComponents[unit.id] ?? []
                  const iSets = unitToISets[unit.id] ?? []
                  return iSets?.length ? (
                    <PanelUnitSection
                      key={unit.id}
                      isLoading={isLoading}
                      siteName={site?.data.name}
                      unit={unit}
                      components={components}
                      indicationSets={iSets}
                    />
                  ) : undefined
                })}
            </>
          </Spin>
        </Content>
      </Layout>
    </Layout>
  )
}

export const IndicationListPanel = React.memo(RawIndicationListPanel)

const PanelComponentSectionListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const UnitTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const KEY_NO_COMPONENT = '@@no-component@@'
const PanelUnitSection: FC<{
  isLoading?: boolean
  siteName: string | undefined
  unit: GeckoUnit
  components: ReadonlyArray<GeckoComponent>
  indicationSets: ReadonlyArray<IndicationSet>
}> = ({ siteName, unit, components, indicationSets }) => {
  const { componentIdToIndicationSets } = useMemo(() => {
    const componentIdToIndicationSets = _.groupBy(
      indicationSets,
      (iSet) => iSet.tags.component ?? KEY_NO_COMPONENT
    )
    return { componentIdToIndicationSets }
  }, [indicationSets])
  const componentPanelCount = useMemo(
    () => Object.entries(componentIdToIndicationSets).length,
    [componentIdToIndicationSets]
  )

  return (
    <>
      <Divider>
        <UnitTitleContainer>
          <Title level={2}>
            {siteName ?? 'Unknown site'} - {unit.data.name}
          </Title>
          <IndicationClassificationCountDisplay
            indicationSets={indicationSets}
          />
        </UnitTitleContainer>
      </Divider>
      <PanelComponentSectionListContainer>
        {componentIdToIndicationSets[KEY_NO_COMPONENT]?.length && (
          <PanelComponentSection
            componentName='Unit-level'
            indicationSets={componentIdToIndicationSets[KEY_NO_COMPONENT]}
            showTitle={componentPanelCount > 1}
          />
        )}
        {(components ?? []).map((component) => {
          const iSets = componentIdToIndicationSets[component.id]
          if (!iSets?.length) {
            return
          }
          return (
            <PanelComponentSection
              key={component.id}
              componentName={component.data.name ?? component.id}
              indicationSets={componentIdToIndicationSets[component.id]}
              showTitle={componentPanelCount > 1}
            />
          )
        })}
      </PanelComponentSectionListContainer>
    </>
  )
}

const ISetCollapseLabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const PanelComponentSection: FC<{
  componentName: string
  indicationSets: ReadonlyArray<IndicationSet>
  showTitle?: boolean
}> = ({ componentName, indicationSets, showTitle }) => {
  const collapseItems = useMemo<CollapseProps['items']>(
    () =>
      indicationSets.map((iSet) => ({
        key: iSet.id,
        label: (
          <ISetCollapseLabelContainer>
            {iSet.title ?? iSet.id}
            <IndicationClassificationCountDisplay indicationSets={[iSet]} />
          </ISetCollapseLabelContainer>
        ),
        children: <IndicationSetSection indicationSet={iSet} />,
      })),
    [indicationSets]
  )

  return (
    <div>
      {showTitle && (
        <>
          <Title level={3}>{componentName}</Title>
          <IndicationClassificationCountDisplay
            indicationSets={indicationSets}
          />
          <Divider plain variant='dashed' />
        </>
      )}
      <Collapse
        items={collapseItems}
        defaultActiveKey={
          collapseItems?.length === 1
            ? (collapseItems[0].key as string)
            : undefined
        }
      />
    </div>
  )
}

const CollapseSpinContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const DEFAULT_PAGE_SIZE = 50

const RawIndicationSetSection: FC<{
  indicationSet: IndicationSet
}> = ({ indicationSet }) => {
  const [pagination, setPagination] = useState<DrilldownPaginationType>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const { query: rawIndicationQuery, isLoading: indicationQueryLoading } =
    useFilteredEntryQuery(RD_TYPE_INDICATION)
  const {
    query: pageQuery,
    isLoading: paginationLoading,
    totalEntries,
  } = usePaginatedQuery(
    useMemo(
      () => ({
        ...rawIndicationQuery,
        tags: {
          ...rawIndicationQuery.tags,
          [RD_TYPE_INDICATION_SET]: indicationSet.id,
        },
      }),
      [indicationSet.id, rawIndicationQuery]
    ),
    pagination
  )
  const { entries, isLoading: pageLoading } = useRolodexEntries(pageQuery)

  const items = useMemo(
    () =>
      entries
        .filter((entry) => entry.type === RD_TYPE_INDICATION)
        ?.map(entryToIndication) ?? [],
    [entries]
  )
  const classificationContextValue = useMemo(
    () =>
      createClassificationIndicationConfigContextValue(
        _.first(indicationSet.classificationConfig) ??
          DEFAULT_CLASSIFICATION_CONFIG
      ),
    [indicationSet.classificationConfig]
  )
  const [selectedIndicationId, setSelectedIndicationId] = useState<
    string | null
  >(null)
  const closeDetail = useCallback(() => setSelectedIndicationId(null), [])
  const selectedIndicationItem = useMemo(
    () => items.find((item) => item.id === selectedIndicationId),
    [items, selectedIndicationId]
  )
  const isLoading = pageLoading || paginationLoading || indicationQueryLoading
  return (
    <div>
      {isLoading && (
        <CollapseSpinContainer>
          <Spin indicator={<LoadingOutlined />} size='large' />
        </CollapseSpinContainer>
      )}
      {!!selectedIndicationId &&
        selectedIndicationItem &&
        indicationSet.detailCards && (
          <RolodexIndicationContextProvider indicationId={selectedIndicationId}>
            <ToggledIndicationDetailView
              isOpen
              close={closeDetail}
              indicationItem={selectedIndicationItem}
              cardConfig={indicationSet.detailCards}
              type='modal'
            />
          </RolodexIndicationContextProvider>
        )}
      <IndicationClassificationConfigContext.Provider
        value={classificationContextValue}>
        {!isLoading && (
          <IndicationTriageList
            loading={isLoading}
            items={items}
            setActiveDetailsItemId={setSelectedIndicationId}
          />
        )}
      </IndicationClassificationConfigContext.Provider>
      {totalEntries > DEFAULT_PAGE_SIZE && (
        <Spin spinning={paginationLoading}>
          <Pagination
            pageSize={pagination.pageSize}
            current={pagination.current}
            total={totalEntries}
            align='end'
            onChange={(page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }))
            }}
          />
        </Spin>
      )}
    </div>
  )
}

const IndicationSetSection = React.memo(RawIndicationSetSection)
